import * as React from "react";
import { useContext } from "react";
import _ from "lodash";
import { GridCellProps } from "@progress/kendo-react-grid";
import { LtlRoutingViewModel } from "../../../../../ViewModel/LtlRoutingViewModel";
import RouteDropDownList from "./RouteDropDownList";
import { EditProjectContext, ProjectStateType } from "../../../../EditProjectContext";
import fixLtlRoutingIdInLtlPipelines from "../../../../FixLtlRoutingIdInLtlPipelines";
import { JsTimeSpan } from "../../../../../ViewModel/JsTimeSpan";
import { isFinished } from "../IsFinished";
import { getLtlPipeline } from "../../GetLtlPipeline";
import { getSupplierTransitTime } from "../../GetSupplierTransitTime";
import { LtlRoutingRouteViewModel } from "../../../../../ViewModel/LtlRoutingRouteViewModel";
import { convertJsTimeSpanToDayJs } from "../../../../../../Utils/timeFormat";

export default function RouteCell(props: GridCellProps) {
  const { field } = props;
  const { dataItem } = props;
  const myField = field || "";

  const editProjectContext = useContext(EditProjectContext);
  const ltlPipeline = getLtlPipeline(editProjectContext);

  if (!dataItem.Route) {
    return (
      <td>
        <RouteDropDownList
          ltlPipeline={ltlPipeline}
          onValueChanged={(route) => {
            setRouteInRoutings({
              newRoute: toRoutingViewModel(route),
              prevRouteChanges: {
                ActualDepartureTime: route.ActualDepartureTime,
              },
              spdrOrPdsrId: ltlPipeline?.SpdrId ?? ltlPipeline?.PdsrId,
              projectStateType: editProjectContext,
            });
          }}
        />
      </td>
    );
  }

  const dataValue = _.get(dataItem, myField);
  return <td>{dataValue}</td>;
}

// DepartureTime.Days are the days needed for transfer in the ODC
function getDepartureDays(predecessor: LtlRoutingViewModel) {
  if (predecessor.DepartureTime?.Days !== undefined) {
    return predecessor.DepartureTime.Days;
  }

  let days = 0;
  let hours = 0;
  if (!predecessor.ArrivalTime) {
    throw new Error("unable to calculate getDepartureDays. predecessor.arrivalTime is null");
  }
  const arrivalTime: JsTimeSpan = predecessor.ArrivalTime;

  if (!predecessor.ODCHandlingTime?.includes(":")) {
    throw new Error(
      'unable to calculate getDepartureDays. Cannot parse ODCHandlingTime in predecessor. Missing char ":" '
    );
  }

  const handlingTime = {
    Minutes: parseInt(predecessor.ODCHandlingTime.split(":")[1], 10),
    Hours: parseInt(predecessor.ODCHandlingTime.split(":")[0], 10),
  };
  let tempMinutes = (arrivalTime.Minutes || 0) + handlingTime.Minutes;

  while (tempMinutes >= 60) {
    hours += 1;
    tempMinutes -= 60;
  }

  let tempHours = (arrivalTime.Hours || 0) + handlingTime.Hours + hours;
  while (tempHours >= 24) {
    days += 1;
    tempHours -= 24;
  }
  return days;
}

function getOutboundDay(predecessor: LtlRoutingViewModel, departureDays: number) {
  if (!predecessor.ActualDepartureTime) {
    throw new Error(
      "unable to calculate getOutboundDay becuase predecessor.ActualDepartureTime is null"
    );
  }
  if (!predecessor.DepartureTime) {
    throw new Error("unable to calculate getOutboundDay becuase predecessor.DepartureTime is null");
  }

  const actualDepartureTime = convertJsTimeSpanToDayJs(predecessor.ActualDepartureTime);
  const departureTime = convertJsTimeSpanToDayJs(predecessor.DepartureTime);
  const arrivalDay = predecessor.ArrivalDay || 0;

  if (
    departureTime.hour() < actualDepartureTime.hour() ||
    (departureTime.hour() === actualDepartureTime.hour() &&
      departureTime.minute() <= actualDepartureTime.minute())
  ) {
    return arrivalDay + departureDays;
  }
  return arrivalDay + 1 + departureDays;
}

function getArrivalDay(
  routingsLength: number,
  predecessorOutboundDay: number,
  transitDays: number
) {
  if (routingsLength > 1) {
    return predecessorOutboundDay + transitDays;
  }
  return transitDays;
}

function setRouteInRoutings({
  newRoute,
  prevRouteChanges,
  spdrOrPdsrId,
  projectStateType,
}: {
  newRoute: LtlRoutingViewModel;
  prevRouteChanges: LtlRoutingViewModel;
  spdrOrPdsrId?: number;
  projectStateType: ProjectStateType;
}) {
  const ltlPipeline = projectStateType.project.ltlPipelines?.find(
    (x) => x.SpdrId === spdrOrPdsrId || x.PdsrId === spdrOrPdsrId
  );
  if (!ltlPipeline) {
    throw new Error(`unable to find ltlPipeline with spdrId or pdsrId ${spdrOrPdsrId}`);
  }

  // push new route
  const routing = [...(ltlPipeline?.routing || []), newRoute];
  if (routing.length > 1) {
    const predecessor = routing[routing.length - 2];

    const departureDays = getDepartureDays(predecessor);
    const outboundDay = getOutboundDay(
      // to set actual departure time in arg
      { ...predecessor, ...prevRouteChanges },
      departureDays
    );

    routing[routing.length - 2] = {
      ...routing[routing.length - 2],
      ...prevRouteChanges,
      OutboundDay: outboundDay,
    };
  }

  const predecessorOutboundDay =
    routing.length > 1 ? routing[routing.length - 2].OutboundDay || 0 : 0;

  newRoute.ArrivalDay = getArrivalDay(
    routing.length,
    predecessorOutboundDay,
    newRoute.TransitDays || 0
  );

  const updateMySupplierTransitTime = getSupplierTransitTime(routing, ltlPipeline.TTAdjustment);

  const updateProject = {
    ...projectStateType.project,
    ltlPipelines: fixLtlRoutingIdInLtlPipelines(
      projectStateType.project.ltlPipelines?.map((x) => {
        if (x.SpdrId === spdrOrPdsrId || x.PdsrId === spdrOrPdsrId) {
          return {
            ...ltlPipeline,
            SupplierTransitTime: updateMySupplierTransitTime.toString(),
            routing,
            IsFinished: isFinished(ltlPipeline, routing),
          };
        }

        return x;
      })
    ),
  };
  projectStateType.setProject(updateProject);
}

function toRoutingViewModel(route: LtlRoutingRouteViewModel): LtlRoutingViewModel {
  return {
    ArrivalDay: route.ArrivalDay,
    ArrivalTime: route.ArrivalTime,
    DepartureTime: route.DepartureTime,
    Route: route,
    Carrier: route.CarrierName,
    IsPlantRoute: route.IsPlantRoute,
    Km: route.Km,
    NextDestination: route.NextDestination,
    NextDestinationGsdb: route.NextDestinationGsdb,
    ODCHandlingTime: route.OdcHandlingTime,
    OutboundDay: route.OutboundDay,
    TransitDays: route.TransitDays,
  };
}
